import { NavigableModal } from '$components'
import { LoaderIcon } from '$components'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { userIndexQueryKey, userShowOptions, userShowQueryKey, userUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { UpdateMemberForm, UpdateMemberSchema } from '../forms/update-member'
import { TeamRoutePath } from '../routes/protected'
import { ToastHelper } from '$components'
import { ToastType } from '$components'
import { Role } from '$api-client/types.gen'

export function UpdateMemberModal() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { id } = useParams()

  const { data: user, isLoading } = useQuery({
    ...userShowOptions({
      path: {
        id: Number(id),
      },
    }),
  })

  const { mutate: updateUser, isPending: isUpdatingUser } = useMutation({
    ...userUpdateMutation(),
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: userShowQueryKey({
          path: {
            id: Number(id),
          },
        }),
      })

      queryClient.invalidateQueries({
        queryKey: userIndexQueryKey(),
      })

      navigate(TeamRoutePath.Team)
      ToastHelper.updateSuccess(ToastType.TeamMember)
    },
    onError: () => ToastHelper.updateError(ToastType.TeamMember),
  })

  const handleClose = () => navigate(TeamRoutePath.Team)

  const handleSubmit = (values: UpdateMemberSchema) => {
    updateUser({
      path: {
        id: Number(id),
      },
      body: {
        first_name: values.first_name,
        last_name: values.last_name,
        role: user?.data.role === Role.SUPER_ADMIN ? undefined : values.role.id as 'Admin' | 'Groomer',
      },
    })
  }

  if (isLoading) {
    return (
      <NavigableModal
        onClose={handleClose}
        className="w-[640px] h-96 flex items-center justify-center"
      >
        <LoaderIcon className="animate-spin" />
      </NavigableModal>
    )
  }

  if (!user?.data) {
    return (
      <NavigableModal
        onClose={handleClose}
        className="w-[640px] h-96 flex items-center justify-center"
      >
        Team member not found.
      </NavigableModal>
    )
  }

  return (
    <NavigableModal onClose={handleClose}>
      <UpdateMemberForm
        isLoading={isUpdatingUser}
        onSubmit={handleSubmit}
        onCancel={handleClose}
        defaultValues={{
          first_name: user.data.first_name,
          last_name: user.data.last_name,
          role: {
            id: user.data.role,
            value: user.data.role,
          },
        }}
      >
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Update Team Member
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <UpdateMemberForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <UpdateMemberForm.Controls />
        </NavigableModal.Footer>
      </UpdateMemberForm>
    </NavigableModal>
  )
}
